import React, { useRef } from 'react';
import { LanguageContext } from '../../utilities/LocalizationModule';
import { Panel, Stack, DefaultButton, Label, PanelType, TextField, PrimaryButton, Spinner, SpinnerSize, Dropdown, IDropdownOption, Icon, StackItem, Callout, FocusTrapZone, Calendar, DayOfWeek, DirectionalHint, Checkbox, MessageBar, MessageBarType, Link } from "@fluentui/react"
import { connect, ConnectedProps } from 'react-redux';
import { setLoading } from "../../redux/modules/user"
import { RootState } from '../../redux';
import { createSubscription, getStripeCatalog, getSubscriptionPriceTiers } from '../../utilities/helpers/ApiHelper';
import { CreateSubscriptionRequest, ITenant, StripeCatalogProductDto, SubscriptionPriceTierResponse } from '../../data-structures/interfaces';
import { SubscriptionIntervalEnum, SubscriptionTierEnum } from '../../data-structures/enums';
import { labelHeadingStyle, labelInfoStyle } from '../../styles/PanelStyle';
import { DayPickerStrings } from '../../utilities/DayPickerStrings';

const mapStateToProps = (state: RootState) => {
    return {
        account: state.user.account,
        isGlobalAdmin: state.user.isGlobalAdmin
    }
}

const mapDispatchToProps = {
    setLoading
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface AddSubscriptionPanelProps extends PropsFromRedux {
    isOpen: boolean,
    closePanel: () => void;
    tenant: ITenant;
    onSuccess: () => void;
    existingSubscriptions?: any[];
    stripeEnabled: boolean;
};

const AddSubscriptionPanel: React.FC<AddSubscriptionPanelProps> = ({
    isOpen,
    closePanel,
    account,
    tenant,
    onSuccess,
    isGlobalAdmin,
    existingSubscriptions,
    stripeEnabled
}: AddSubscriptionPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    // const minLicensesInvoice = "25";

    // Non-global-admin users don't see the payment-method dropdown — so the initial
    // state value is what actually gets submitted for them. Default them to Stripe.
    // Global admins keep the historical invoice_static default and can change it via
    // the dropdown.
    const [selectedMethod, setSelectedMethod] = React.useState<IDropdownOption>(
        isGlobalAdmin
            ? { key: "invoice_static", text: languageStrings.InvoiceStatic }
            : { key: "stripe", text: "Stripe" }
    );
    const [selectedSubscription, setSelectedSubscription] = React.useState<IDropdownOption>();

    const [subscriptionOptions, setSubscriptionOptions] = React.useState<IDropdownOption[]>([]);
    const [subscriptionTierOptions, setSubscriptionTierOptions] = React.useState<IDropdownOption[]>([
        { key: SubscriptionTierEnum.Paid, text: SubscriptionTierEnum.Paid }, { key: SubscriptionTierEnum.Trial, text: SubscriptionTierEnum.Trial }
    ]);
    const [selectedSubscriptionTier, setSelectedSubscriptionTier] = React.useState<IDropdownOption>();

    const [showCalendar, setShowCalendar] = React.useState<boolean>(false);
    const [selectedDate, setSelectedDate] = React.useState<Date>(null);

    const [isCreatingPayment, setIsCreatingPayment] = React.useState(false);

    const [subscriptionPriceTiers, setSubscriptionPriceTiers] = React.useState<SubscriptionPriceTierResponse[]>([]);

    // Seat floor for the seat-based Docubird products (Professional / Essentials). Service
    // add-ons are exempt (no seat field is shown for them). Mirrors the server-side minimum
    // enforced in StripeBillingService / SubscriptionService.
    const MIN_NON_SERVICE_SEATS = 10;
    const [licenceCount, setLicenceCount] = React.useState<number>(MIN_NON_SERVICE_SEATS);
    const [invoiceTermsAccepted, setInvoiceTermsAccepted] = React.useState<boolean>(false);
    const [billingEmail, setBillingEmail] = React.useState<string>(account?.username ?? "");
    // The Stripe Product ID (e.g. "prod_…") the admin picked. This is the canonical
    // key for the Checkout request — the backend resolves the Stripe Price ID via the
    // StripeProductCatalog table (admin-managed under Manage Stripe Products) and
    // discovers prices live from Stripe at Checkout time.
    const [stripeProductId, setStripeProductId] = React.useState<string | undefined>(undefined);
    const [stripeCatalog, setStripeCatalog] = React.useState<StripeCatalogProductDto[]>([]);

    // Set after a successful POST /subscriptions/create on the Stripe path. When
    // set, the form is replaced by a confirmation view that surfaces the Checkout
    // URL as a clickable fallback (so a blocked popup doesn't strand the admin)
    // and lets them dismiss the panel via a Done button. The subscription list
    // refreshes in the background via the webhook → SignalR pipeline.
    const [stripeCheckoutState, setStripeCheckoutState] = React.useState<{
        url: string;
    } | null>(null);

    // Informational only: show a warning when the admin is about to create a second
    // Stripe subscription on the EXACT same Stripe product the tenant already has a
    // non-terminal sub for. The button stays enabled — multi-active-sub-per-product is
    // allowed by design.
    //
    // Compare by Stripe Product ID directly so e.g. Signing and PDF Overlay are
    // distinguishable even though they both project to internal SubscriptionProductEnum
    // Service=3 in the local DB. Legacy rows without StripeProductId (pre-migration or
    // not yet backfilled) carry null and never match — the warning under-fires for
    // unbackfilled rows rather than over-firing on the wrong product.
    const hasExistingActiveForSelectedProduct = React.useMemo(() => {
        if (!stripeProductId || !selectedMethod?.key) return false;
        if ((selectedMethod.key as string)?.toLowerCase() !== "stripe") return false;
        return (existingSubscriptions ?? []).some(s =>
            s?.stripeProductId === stripeProductId &&
            (s?.method as string)?.toLowerCase() === "stripe" &&
            ["active", "pending", "suspended"].includes((s?.status as string)?.toLowerCase())
        );
    }, [stripeProductId, selectedMethod, existingSubscriptions]);

    const calendarButtonElement = useRef(null);

    const paymentMethodOptions: IDropdownOption[] = [
        { key: "invoice_static", text: languageStrings.InvoiceStatic },
        ...(stripeEnabled ? [{ key: "stripe", text: "Stripe" }] : [])
    ];

    const onMethodChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedMethod(item);
        // Reset Stripe-specific selections when switching methods so the form
        // does not retain a stale (stripeProductId, interval) pair from a different method.
        setStripeProductId(undefined);
        setSelectedSubscription(undefined);
    };

    const selectedStripeProduct = React.useMemo(
        () => stripeProductId ? stripeCatalog.find(p => p.stripeProductId === stripeProductId) : undefined,
        [stripeProductId, stripeCatalog]
    );

    // SubscriptionProductEnum.Service = 3. Service add-ons are billed as a single
    // recurring item (e.g. Signing, PDF Overlay) with no seat semantics — the License
    // Count field doesn't apply, so we hide it and submit a null seat count. The server
    // resolves a Stripe Quantity of 1 in that case.
    const isServiceStripeProduct = selectedStripeProduct?.internalProductId === 3;

    // The seat floor applies to seat-based Docubird products (Professional / Essentials) on the
    // Stripe path only. Service add-ons and the invoice methods are exempt — mirrors the
    // server-side rule (StripeBillingService.MinimumNonServiceSeatCount).
    const enforceSeatMinimum = selectedMethod?.key === "stripe"
        && selectedSubscriptionTier?.key === SubscriptionTierEnum.Paid
        && !isServiceStripeProduct;

    const stripeProductOptions: IDropdownOption[] = React.useMemo(
        () => stripeCatalog.map(p => ({
            key: p.stripeProductId,
            text: p.managedPaymentsReady ? p.name : `${p.name} (tax code missing)`,
            disabled: !p.managedPaymentsReady
        })),
        [stripeCatalog]
    );

    // const [priceToPay, setPriceToPay] = React.useState("");

    React.useEffect(() => {
        (async () => {
            const tiers = await getSubscriptionPriceTiers();
            if (tiers?.length > 0) {
                setSubscriptionPriceTiers(tiers);
                // setSubscriptionOptions([
                //     { key: 2, text: languageStrings.PricePerLicenseYear.replace("{price}", tiers[0].licensePrice.toFixed(2)), },
                //     { key: 1, text: languageStrings.PricePerLicenseMonth.replace("{price}", tiers[0].licensePrice.toFixed(2)) }
                // ])
                setSubscriptionOptions([
                    { key: 2, text: languageStrings.Yearly },
                    { key: 1, text: languageStrings.Monthly }
                ])
            }
        })()
    }, []);

    // Load the Stripe catalog once. The list is small (typically <10 products)
    // so we don't re-fetch on every method switch.
    React.useEffect(() => {
        (async () => {
            const catalog = await getStripeCatalog();
            setStripeCatalog(catalog ?? []);
        })();
    }, []);

    // If Stripe is off, never leave the form on the (now-hidden) Stripe method.
    React.useEffect(() => {
        if (!stripeEnabled && (selectedMethod?.key as string)?.toLowerCase() === "stripe") {
            setSelectedMethod({ key: "invoice_static", text: languageStrings.InvoiceStatic });
        }
    }, [stripeEnabled, selectedMethod, languageStrings]);

    // Gate the interval dropdown based on what's configured for the selected Stripe product.
    // For non-Stripe methods (and Stripe without a product selected), the full Monthly/Yearly
    // set is offered as before.
    React.useEffect(() => {
        if (selectedMethod?.key === "stripe" && selectedStripeProduct) {
            if (!selectedStripeProduct.managedPaymentsReady) {
                setSubscriptionOptions([]);
                setSelectedSubscription(undefined);
                return;
            }
            const options: IDropdownOption[] = [];
            if (selectedStripeProduct.yearlyAvailable) {
                options.push({ key: SubscriptionIntervalEnum.Yearly, text: languageStrings.Yearly });
            }
            if (selectedStripeProduct.monthlyAvailable) {
                options.push({ key: SubscriptionIntervalEnum.Monthly, text: languageStrings.Monthly });
            }
            setSubscriptionOptions(options);

            // Drop the current selection if it's no longer offered by this product.
            if (selectedSubscription) {
                const stillValid = options.some(o => o.key === selectedSubscription.key);
                if (!stillValid) {
                    setSelectedSubscription(undefined);
                }
            }
            // If exactly one interval is offered, pre-select it so the admin doesn't
            // have to make a forced choice for a product that only has one option.
            if (options.length === 1 && (!selectedSubscription || selectedSubscription.key !== options[0].key)) {
                setSelectedSubscription(options[0]);
            }
        } else {
            // Default (non-Stripe, or Stripe without a product picked): both intervals.
            setSubscriptionOptions([
                { key: SubscriptionIntervalEnum.Yearly, text: languageStrings.Yearly },
                { key: SubscriptionIntervalEnum.Monthly, text: languageStrings.Monthly }
            ]);
        }
    }, [selectedMethod, selectedStripeProduct, languageStrings]);

    // React.useEffect(() => {
    //     if (subscriptionOptions?.length > 0) {
    //         let tier = subscriptionPriceTiers.find(x => x.minUsers <= licenceCount && x.maxUsers >= licenceCount);
    //         if (!tier) {
    //             tier = subscriptionPriceTiers[0]
    //         }

    //         const monthlyMultiplier = 1.1;
    //         const monthlyPrice = licenceCount * tier.licensePrice * monthlyMultiplier;
    //         const annualPrice = licenceCount * tier.licensePrice * 12;

    //         // Determine the selected subscription interval.
    //         const selectedInterval = selectedSubscription ? selectedSubscription.key : undefined;

    //         // Set the total price to be displayed on the button.
    //         const price = selectedInterval === SubscriptionIntervalEnum.Monthly ? monthlyPrice : annualPrice;
    //         setPriceToPay(price.toFixed(2));

    //         // Update the dropdown options with the updated prices.
    //         setSubscriptionOptions([
    //             { key: 2, text: languageStrings.PricePerLicenseYear.replace("{price}", tier.licensePrice.toFixed(2)), },
    //             { key: 1, text: languageStrings.PricePerLicenseMonth.replace("{price}", (tier.licensePrice * monthlyMultiplier).toFixed(2)) }
    //         ]);
    //     }
    // }, [licenceCount, subscriptionPriceTiers, selectedSubscription]);

    React.useEffect(() => {
        if (subscriptionTierOptions?.length > 0) {
            setSelectedSubscriptionTier(subscriptionTierOptions[0])
        }
    }, [subscriptionTierOptions]);

    const onSubscriptionChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedSubscription(item);
    };

    const onSubscriptionTierChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setSelectedSubscriptionTier(item);
    };

    const isPayButtonDisabled = () => {
        if (isCreatingPayment) {
            return true;
        }

        if (selectedSubscriptionTier?.key === SubscriptionTierEnum.Trial) {
            return !!!selectedDate;
        }

        if (selectedMethod?.key === "invoice_dynamic" && !invoiceTermsAccepted) {
            return true;
        }

        // Seat floor — Stripe path only (invoice methods and Service add-ons are exempt, matching
        // the server-side rule). Block submit below the minimum.
        if (enforceSeatMinimum && licenceCount < MIN_NON_SERVICE_SEATS) {
            return true;
        }

        // Stripe requires an explicit product pick AND a billing email AND a valid interval.
        // The interval must be one of the intervals actually configured for that product.
        if (selectedMethod?.key === "stripe") {
            if (!selectedStripeProduct) return true;
            if (!selectedStripeProduct.managedPaymentsReady) return true;
            if (!billingEmail || billingEmail.trim().length === 0) return true;
            if (!selectedSubscription) return true;
            const intervalAvailable = (selectedSubscription.key === SubscriptionIntervalEnum.Monthly && selectedStripeProduct.monthlyAvailable)
                || (selectedSubscription.key === SubscriptionIntervalEnum.Yearly && selectedStripeProduct.yearlyAvailable);
            if (!intervalAvailable) return true;
            return false;
        }

        if (selectedMethod && selectedSubscription) {
            return false;
        }

        return true;
    }

    const onSelectDate = (date: Date): void => {
        setShowCalendar(!showCalendar);
        setSelectedDate(date);
    };

    // const getButtonText = () => {
    //     if (selectedMethod?.key === "invoice_dynamic") {
    //         return languageStrings.AddSubscription;
    //     }

    //     if (selectedSubscriptionTier?.key === SubscriptionTierEnum.Trial) {
    //         return languageStrings.AddSubscription;
    //     }

    //     if (priceToPay) {
    //         return `${languageStrings.AddSubscription} (€${priceToPay})`;
    //     }

    //     return languageStrings.AddSubscription;
    // }

    return (
        <Panel
            headerText={languageStrings.AddSubscription}
            isOpen={isOpen}
            isLightDismiss
            onDismiss={() => {
                closePanel();
            }}
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={"500px"}
        >
            <Stack tokens={{ childrenGap: 20 }} style={{ marginTop: 20 }}>
                <Stack.Item>
                    <Label styles={labelHeadingStyle}>{languageStrings.Tenant}</Label>
                    <Label styles={labelInfoStyle} disabled>
                        {tenant?.friendlyName}
                    </Label>
                    <Label styles={labelInfoStyle} disabled>
                        {tenant?.tenantId}
                    </Label>
                </Stack.Item>
                {!stripeCheckoutState && <>
                {isGlobalAdmin && <>
                    <Dropdown
                        label={languageStrings.SubscriptionTier}
                        options={subscriptionTierOptions}
                        onChange={onSubscriptionTierChange}
                        defaultSelectedKey={SubscriptionTierEnum.Paid}
                    />
                    {selectedSubscriptionTier?.key === SubscriptionTierEnum.Trial && <Stack.Item style={{ paddingTop: 5 }}>
                        <Label styles={labelHeadingStyle}>{languageStrings.LicenseExpiryDate}</Label>
                        <div ref={calendarButtonElement}>
                            <DefaultButton
                                onClick={() => {
                                    setShowCalendar(true);
                                }}
                                text={!selectedDate ? languageStrings.ClickForCalendar : new Date(selectedDate).toDateString()}
                            />
                        </div>
                        {showCalendar && (
                            <Callout
                                isBeakVisible={false}
                                className="ms-DatePicker-callout"
                                gapSpace={0}
                                doNotLayer={false}
                                target={calendarButtonElement}
                                directionalHint={DirectionalHint.rightCenter}
                                onDismiss={() => setShowCalendar(false)}
                                setInitialFocus
                            >
                                <FocusTrapZone firstFocusableTarget="ms-DatePicker-day--today" isClickableOutsideFocusTrap>
                                    <Calendar
                                        // eslint-disable-next-line react/jsx-no-bind
                                        onSelectDate={onSelectDate}
                                        onDismiss={() => setShowCalendar(false)}
                                        isMonthPickerVisible={true}
                                        minDate={new Date()}
                                        value={new Date()}
                                        firstDayOfWeek={DayOfWeek.Sunday}
                                        strings={DayPickerStrings}
                                        isDayPickerVisible={true}
                                        highlightCurrentMonth={true}
                                        highlightSelectedMonth={true}
                                        showMonthPickerAsOverlay={true}
                                        showGoToToday={true}
                                    />
                                </FocusTrapZone>
                            </Callout>
                        )}
                    </Stack.Item >}
                </>}

                {selectedSubscriptionTier?.key === SubscriptionTierEnum.Paid && <>
                    {isGlobalAdmin && <Dropdown
                        label="Payment method"
                        options={paymentMethodOptions}
                        onChange={onMethodChange}
                        selectedKey={selectedMethod?.key}
                    />}
                    {selectedMethod?.key === "stripe" && (
                        stripeProductOptions.length === 0
                            ? <Label styles={labelInfoStyle}>
                                No Stripe catalog products are configured. Open
                                <strong> Manage Stripe products</strong> from the toolbar
                                to add or sync products, then re-open this panel.
                            </Label>
                            : <Dropdown
                                placeholder="Select a product"
                                label="Product"
                                options={stripeProductOptions}
                                onChange={(_e, item) => setStripeProductId(item?.key as string)}
                                selectedKey={stripeProductId}
                            />
                    )}
                    <Dropdown
                        placeholder={languageStrings.SelectSubscription}
                        label={languageStrings.SubscriptionType}
                        options={subscriptionOptions}
                        onChange={onSubscriptionChange}
                        selectedKey={selectedSubscription?.key}
                        disabled={selectedMethod?.key === "stripe" && (!selectedStripeProduct || !selectedStripeProduct.managedPaymentsReady)}
                    />
                    {selectedMethod?.key === "stripe" && selectedStripeProduct && (
                        !selectedStripeProduct.managedPaymentsReady
                            ? <Label styles={labelInfoStyle}>
                                This product needs a Stripe tax code before it can be sold through Managed Payments.
                            </Label>
                            : !selectedStripeProduct.monthlyAvailable && !selectedStripeProduct.yearlyAvailable
                            ? <Label styles={labelInfoStyle}>
                                This product has no active monthly or yearly recurring prices in Stripe.
                            </Label>
                            : <Label styles={labelInfoStyle}>
                                {selectedStripeProduct.monthlyAvailable && selectedStripeProduct.yearlyAvailable
                                    ? "Both monthly and yearly billing are offered for this product."
                                    : selectedStripeProduct.monthlyAvailable
                                        ? "This product is monthly-only."
                                        : "This product is yearly-only."}
                            </Label>
                    )}
                    {selectedMethod?.key === "stripe" && (
                        <TextField
                            label="Billing email"
                            description="This address receives Stripe billing emails and is used for Link billing management. Different products under the same tenant can use different billing emails — each unique (email, currency) gets its own Stripe customer."
                            type="email"
                            value={billingEmail}
                            onChange={(_e, v) => setBillingEmail(v ?? "")}
                        />
                    )}
                    {/* {selectedSubscription && <Dropdown
                        placeholder={languageStrings.SelectPaymentMethod}
                        options={paymentMethodOptions}
                        onChange={onMethodChange}
                        label={languageStrings.PaymentMethod}
                        onRenderOption={(option) => (
                            <>
                                {option.data ?
                                    <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign='center'>
                                        <img src={option.data.image.size1x} alt={option.data.description} style={{ width: '20px', marginRight: '10px' }} />
                                        {option.data.description}
                                    </Stack> :
                                    <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign='center'>
                                        <StackItem style={{ paddingLeft: 4 }}>
                                            <Icon iconName='M365InvoicingLogo' />
                                        </StackItem>
                                        <StackItem>
                                            {option.text}
                                        </StackItem>
                                    </Stack>}

                            </>

                        )}
                        onRenderTitle={(selectedOptions) => {
                            const selectedOption = selectedOptions[0];
                            setSelectedMethod(selectedOption);
                            return (
                                <>
                                    {!(selectedOption.key as string).includes("invoice") ?
                                        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                                            <img src={selectedOption.data.image.size1x} alt={selectedOption.data.description} style={{ width: '20px', marginRight: '10px' }} />
                                            {selectedOption.data.description}
                                        </Stack> :
                                        <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign='center'>
                                            <StackItem style={{ paddingLeft: 4 }}>
                                                <Icon iconName='M365InvoicingLogo' />
                                            </StackItem>
                                            <StackItem>
                                                {selectedOption.text}
                                            </StackItem>
                                        </Stack>
                                    }
                                </>

                            );
                        }}
                    />} */}
                </>}


                {selectedSubscription && selectedMethod && selectedMethod.key !== "invoice_dynamic" && selectedSubscriptionTier?.key === SubscriptionTierEnum.Paid && !isServiceStripeProduct && <TextField
                    label={languageStrings.LicenseCount}
                    type='number'
                    min={enforceSeatMinimum ? MIN_NON_SERVICE_SEATS : 1}
                    value={licenceCount.toString()}
                    onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                        let newLicenceCount = +newValue;
                        if (newLicenceCount <= 0) {
                            newLicenceCount = 1
                        }
                        setLicenceCount(newLicenceCount);
                    }}
                    // Stripe-only floor. Don't bump on every keystroke (hostile while typing, e.g.
                    // "1" → "15"); surface the floor as an error and block submit via isPayButtonDisabled.
                    errorMessage={enforceSeatMinimum && licenceCount < MIN_NON_SERVICE_SEATS ? `Minimum ${MIN_NON_SERVICE_SEATS} seats.` : undefined}
                />}

                {/* {selectedMethod?.key === "invoice_dynamic" && selectedSubscriptionTier?.key === SubscriptionTierEnum.Paid && <Checkbox label={languageStrings.MinimumLicenseCharge.replace("{amount}", minLicensesInvoice)} checked={invoiceTermsAccepted} onChange={() => setInvoiceTermsAccepted(prev => !prev)} />} */}

                {hasExistingActiveForSelectedProduct && (
                    <MessageBar
                        messageBarType={MessageBarType.warning}
                        styles={{ root: { marginTop: 12 } }}
                    >
                        {languageStrings.DuplicateActiveSubscriptionWarning}
                    </MessageBar>
                )}

                <PrimaryButton
                        disabled={isPayButtonDisabled()}
                        text={languageStrings.AddSubscription}
                        onClick={async () => {
                            setIsCreatingPayment(true);

                            try {
                                const request: CreateSubscriptionRequest = {
                                    tenantId: tenant.id,
                                    accountEmail: account.username,
                                    method: selectedMethod?.key as string,
                                    interval: selectedSubscriptionTier.key === SubscriptionTierEnum.Trial ? SubscriptionIntervalEnum.OnceOff : selectedSubscription.key as number,
                                    lisenceCount: isServiceStripeProduct ? null : licenceCount,
                                    expiryDate: selectedDate,
                                    tier: selectedSubscriptionTier.key as string,
                                    productId: selectedMethod?.key === "stripe" ? stripeProductId : undefined,
                                    billingEmail: selectedMethod?.key === "stripe" ? billingEmail : undefined
                                }

                                const paymentResult = await createSubscription(request);
                                const isStripe = selectedMethod?.key === "stripe";

                                if (isStripe && paymentResult) {
                                    // Open Stripe Checkout in a new tab. We deliberately do NOT
                                    // inspect window.open's return value: with noopener/noreferrer
                                    // the spec mandates a null return even on success (no usable
                                    // Window reference can be handed back to the opener), so
                                    // "!popup" is not a reliable blocked-popup signal. Instead
                                    // we always render the fallback link in the confirmation
                                    // view so the admin can re-launch Checkout manually if the
                                    // tab didn't actually open. The subscription list refreshes
                                    // via the webhook → SignalR pipeline regardless of which
                                    // tab the customer pays in.
                                    window.open(paymentResult, "_blank", "noopener,noreferrer");
                                    setStripeCheckoutState({ url: paymentResult });
                                    onSuccess();
                                    // Intentionally NOT closing the panel — the user dismisses
                                    // it via the Done button so they retain access to the link.
                                } else {
                                    // Invoice / trial: backend created the row synchronously.
                                    onSuccess();
                                    closePanel();
                                }
                            } catch (error) {
                                // Ignore
                            } finally {
                                setIsCreatingPayment(false);
                            }
                        }} >
                        {isCreatingPayment && <Spinner size={SpinnerSize.small} style={{ paddingRight: 15 }} />}
                    </PrimaryButton>
                </>}

                {stripeCheckoutState && <>
                    <MessageBar messageBarType={MessageBarType.success}>
                        Stripe Checkout has opened in a new tab. Complete the payment there — this subscription list refreshes automatically once Stripe confirms. If the tab didn't open, use the link below.
                    </MessageBar>
                    <Stack.Item>
                        <Label styles={labelHeadingStyle}>Stripe Checkout link</Label>
                        <Link href={stripeCheckoutState.url} target="_blank" rel="noopener noreferrer">
                            Open Stripe Checkout
                        </Link>
                    </Stack.Item>
                    <PrimaryButton
                        text="Done"
                        onClick={closePanel}
                    />
                </>}
            </Stack>

        </Panel>
    )
}

export default connector(AddSubscriptionPanel);
